import { ReactNode } from 'react';

import clsx from 'clsx';

import { TextAlignment } from '@lessonPlanner/constants';
import { REGEX } from '@lessonPlanner/regex';

interface ContentItem {
  text: string;
  isBullet?: boolean;
  number?: string;
}

interface ContentObject {
  image?: string;
  content: string;
}

interface PptContentProps {
  isPreview?: boolean;
  content?: string | ContentObject;
  className?: string;
  textColor?: string;
  fontFamily?: string;
  typography?: {
    fontSize?: string;
    fontWeight?: string;
    lineHeight?: string;
    textAlign?: TextAlignment;
  };
  layout?: string;
}

export function PptContent({
  isPreview = false,
  content = '',
  className = '',
  textColor = '#000',
  fontFamily,
  typography = {},
  layout = '',
}: PptContentProps) {
  const hasFormatting = (text: string) => {
    return (
      text?.includes('[') && (text?.includes('(*)') || /\(\d+\)/.test(text))
    );
  };

  const parseContent = (content: string): ContentItem[] => {
    const parseNumberedItems = (content: string): ContentItem[] => {
      return (
        content?.match(REGEX.NUMBERED_ITEMS)?.map((item) => {
          const trimmedItem = item.trim();
          const numberMatch = trimmedItem.match(REGEX.NUMBER_PATTERN);
          const number = numberMatch ? numberMatch[1] : undefined;
          const text = number
            ? trimmedItem.replace(numberMatch[0], '')
            : trimmedItem;

          return { text, number };
        }) || [{ text: content }]
      );
    };

    const parseFormattedItems = (content: string): ContentItem[] => {
      return content
        .split('[')
        .filter(Boolean)
        .map((item) => {
          const trimmedItem = item.trim();
          const text = trimmedItem.split(']')[0];
          const format = trimmedItem.split(']')[1]?.trim();

          return {
            text,
            isBullet: format?.includes('(*)'),
            number: format?.match(/\((\d+)\)/)?.[1],
          };
        });
    };

    return hasFormatting(content)
      ? parseFormattedItems(content)
      : parseNumberedItems(content);
  };

  const renderContentItem = (item: ContentItem, index: number): ReactNode => {
    const typographyStyles = {
      color: textColor,
      fontFamily,
      fontSize: typography.fontSize || (isPreview ? undefined : getDefaultFontSize()),
      fontWeight: typography.fontWeight,
      lineHeight: typography.lineHeight,
      textAlign: typography.textAlign,
    };

    if (item.isBullet) {
      const bulletSize = isPreview ? 'min(0.25cqw, 0.25cqh)' : 'min(2cqw, 2cqh)';
      const bulletMargin = isPreview ? 'min(0.25cqh)' : 'min(1.2em)';

      return (
        <div key={index} className="flex items-center gap-1 text-left sm:gap-2">
          <span
            className="flex-shrink-0 rounded-full mt-0.5"
            style={{
              backgroundColor: textColor,
              width: bulletSize,
              height: bulletSize,
            }}
          />
          <span className={className} style={typographyStyles}>
            {item.text}
          </span>
        </div>
      );
    }

    if (item.number) {
      return (
        <div key={index} className="flex items-start gap-1 sm:gap-2">
          <span
            className={`flex-shrink-0 select-none font-medium ${className}`}
            style={{ color: textColor, fontFamily }}
          >
            {item.number}.
          </span>
          <span className={className} style={typographyStyles}>
            {item.text}
          </span>
        </div>
      );
    }

    return (
      <p key={index} className={className} style={typographyStyles}>
        {item.text}
      </p>
    );
  };

  const getContentString = (content: string | ContentObject): string => {
    if (typeof content === 'string') {
      return content;
    }
    return content.content || '';
  };

  const getDefaultFontSize = () => {
    if (isPreview) return undefined;
    
    if (layout === 'leftContent' || layout === 'rightContent') {
      return 'min(3.1cqw,6.2cqh)';
    }
    
    return undefined;
  };

  const contentItems = parseContent(getContentString(content));

  const isContentSideLayout = !isPreview && (layout === 'leftContent' || layout === 'rightContent');
  const fontSizeClass = isContentSideLayout ? 'text-[min(3.1cqw,6.2cqh)]' : 'text-[min(3cqw,6cqh)]';

  return (
    <div
      style={{ textAlign: typography.textAlign }}
      className={clsx('space-y-1', className, {
        [fontSizeClass]: !isPreview && !className.includes('text-[') && !className.includes('text-center'),
      })}
    >
      {contentItems.map((item, index) => renderContentItem(item, index))}
    </div>
  );
}
